import React, { useCallback } from 'react';
import { StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { BackLink } from './BackLink';

interface ScreenBackLinkProps {
  label?: string;
  /** Override the default navigation.goBack() behavior. When provided, the link
   *  always renders (caller is responsible for deciding when to show it). */
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
}

export const ScreenBackLink: React.FC<ScreenBackLinkProps> = ({
  label = 'Back',
  onPress,
  style,
}) => {
  const navigation = useNavigation();
  const { semantic } = useThemeColors();
  // Only consider whether THIS navigator can go back — deliberately NOT
  // navigation.canGoBack(), which also returns true when a *parent* navigator
  // can go back. A screen at index 0 of its own stack (e.g. a top-level section
  // reached via a stack reset) would otherwise flash a back link the moment a
  // modal is pushed on the root stack: it stays mounted and visible under the
  // modal during the open transition, and the parent becoming able to go back
  // flips canGoBack() to true. Reading this navigator's own index keeps the
  // check scoped and re-renders only on this navigator's state changes.
  const canGoBackHere = useNavigationState((state) => (state?.index ?? 0) > 0);

  const handlePress = useCallback(() => {
    if (onPress) {
      onPress();
      return;
    }
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [onPress, navigation]);

  if (!onPress && !canGoBackHere) return null;

  return (
    <BackLink
      label={label}
      onPress={handlePress}
      color={semantic.interactive.primary}
      style={[styles.default, style]}
    />
  );
};

const styles = StyleSheet.create({
  default: {
    marginTop: 16,
    marginBottom: 4,
  },
});

export default ScreenBackLink;
